// src/components/ProtectedRoute.js
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Crown, Sparkles } from 'lucide-react';
import { cleanupExpiredAccount } from '../config/stripe';
import { ACCOUNT_CONFIG } from '../config/accountConfig';
import { getChildren, getActiveChildren } from '../utils/childrenHelper';
import { getTeacherByAuthUid } from '../utils/teacherHelper';
import { TRIAL_SIGNUP_ENABLED } from '../config/featureFlags';

function ProtectedRoute({ children }) {
  const { user, authLoading } = useAuth();
  const [courseState, setCourseState] = useState({
    isLoading: true,
    courseStatus: null,
    hasAccess: false
  });

  useEffect(() => {
    const checkCourseAccess = async () => {
      if (authLoading) return;
      
      if (!user) {
        setCourseState({
          isLoading: false,
          courseStatus: null,
          hasAccess: false
        });
        return;
      }

      try {
        // Get user's course status from Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (!userDoc.exists()) {
          // No parent/user doc — this may be a teacher account. Flag it so
          // the render branch can route to the portal instead of signup.
          try {
            const teacher = await getTeacherByAuthUid(user.uid);
            if (teacher) {
              setCourseState({
                isLoading: false,
                courseStatus: 'teacher',
                hasAccess: false
              });
              return;
            }
          } catch (teacherLookupErr) {
            console.error('ProtectedRoute teacher lookup error:', teacherLookupErr);
            // fall through to no-document on error
          }
          setCourseState({
            isLoading: false,
            courseStatus: 'no-document',
            hasAccess: false
          });
          return;
        }

const userData = userDoc.data();
        
        // ✅ MULTI-CHILD SUPPORT: Use childrenHelper to get all children
        const allChildren = getChildren(userData);
        
        console.log('📊 ProtectedRoute - User Data Structure:', {
          hasChildrenArray: !!userData.children,
          hasRootCourse: !!userData.course,
          totalChildren: allChildren.length,
          children: allChildren.map(c => ({
            id: c.id,
            name: c.name,
            courseStatus: c.course?.status,
            courseEndDate: c.course?.endDate
          }))
        });

        // ── Trial guard — ABOVE the course-status ladder below ──────────────
        // A parent who signed up through /trial has children[] entries with
        // course: null (no course object at all). Route them to the trial
        // status screen instead of the payment funnel.
        //
        // BOTH conditions are required. !hasAnyCourse alone is NOT sufficient:
        // a legacy or long-abandoned account can also have no course, and must
        // still fall through to the ladder's default deny. signupSource is
        // written only when createTrialSignup creates the user doc; once a
        // trial converts (Phase 2) the child gains a course, hasAnyCourse
        // becomes true, and this guard stops firing even though signupSource
        // stays 'trial' forever.
        //
        // TRIAL_SIGNUP_ENABLED is a PRE-LAUNCH switch, not an emergency
        // rollback. Flag-off is byte-identical to pre-Phase-1 ONLY while zero
        // trial accounts exist. Once real ones do, turning it off strands them:
        // this guard stops firing → the ladder's default deny → the same
        // flag-gated redirect in CompleteSubscription no longer rescues them →
        // they land on a payment page for a course they never chose. Rolling
        // back after launch means migrating or manually handling those accounts.
        const hasAnyCourse = allChildren.some(c => !!c.course);
        if (TRIAL_SIGNUP_ENABLED && !hasAnyCourse && userData.signupSource === 'trial') {
          setCourseState({ isLoading: false, courseStatus: 'trial', hasAccess: false });
          return;
        }

        // Check if user has any children at all
        if (allChildren.length === 0) {
          setCourseState({
            isLoading: false,
            courseStatus: 'no-course',
            hasAccess: false
          });
          return;
        }

        // ✅ MULTI-CHILD LOGIC: Check status for ALL children
        // Get children by status
        const pendingChildren   = allChildren.filter(child => child.course?.status === 'pending');
        const activeChildren    = allChildren.filter(child => child.course?.status === 'active');
        const expiredChildren   = allChildren.filter(child => child.course?.status === 'expired');
        const completedChildren = allChildren.filter(child => child.course?.status === 'completed');

        console.log('📊 Children Status Breakdown:', {
          pending: pendingChildren.length,
          active: activeChildren.length,
          expired: expiredChildren.length,
          completed: completedChildren.length,
          total: allChildren.length
        });

        // SCENARIO 1: ALL children are pending (no payment completed yet)
        if (pendingChildren.length === allChildren.length) {
          // Check if pending account has expired (7 days in accountConfig)
          const oldestPendingChild = pendingChildren[0];
          const createdAt = oldestPendingChild.course?.createdAt || userData.createdAt;
          
          if (!createdAt) {
            // No creation date - allow fresh start
            setCourseState({
              isLoading: false,
              courseStatus: 'expired-pending',
              hasAccess: false
            });
            return;
          }
          
          const now = new Date();
          const createdDate = new Date(createdAt);
          const daysSinceCreation = (now - createdDate) / (1000 * 60 * 60 * 24);
          
          if (daysSinceCreation > ACCOUNT_CONFIG.PENDING_EXPIRY_DAYS) {
            // Account expired - trigger cleanup
            try {
              await cleanupExpiredAccount(user.uid);
            } catch (error) {
              console.error('Cleanup failed:', error);
            }
            
            setCourseState({
              isLoading: false,
              courseStatus: 'expired-pending',
              hasAccess: false
            });
            return;
          }
          
          // Still within x-day window - show payment page
          setCourseState({
            isLoading: false,
            courseStatus: 'pending',
            hasAccess: false
          });
          return;
        }

        // SCENARIO 2: At least ONE child has an ACTIVE course
        // Grant access whenever ANY child is 'active'. The course end date does
        // NOT gate access — game lockout is driven only by session completion
        // (status 'completed') or admin deactivation (stored status 'expired').
        if (activeChildren.length > 0) {
          setCourseState({
            isLoading: false,
            courseStatus: 'active',
            hasAccess: true
          });
          return;
        }

        // SCENARIO 3: NO active children, at least one COMPLETED child
        // Show the celebratory "course complete" screen. This covers single-child
        // accounts where the only child finished their sessions, and multi-child
        // accounts where no active siblings remain.
        if (completedChildren.length > 0) {
          setCourseState({
            isLoading: false,
            courseStatus: 'completed',
            hasAccess: false
          });
          return;
        }

        // SCENARIO 4: NO active children - either all expired or mix of pending/expired
        // If all children are expired
        if (expiredChildren.length === allChildren.length) {
          setCourseState({
            isLoading: false,
            courseStatus: 'expired',
            hasAccess: false
          });
          return;
        }
        
        // Mixed pending/expired - redirect to payment for pending ones
        if (pendingChildren.length > 0) {
          setCourseState({
            isLoading: false,
            courseStatus: 'pending',
            hasAccess: false
          });
          return;
        }

        // Default fallback: no access
        console.warn('⚠️ ProtectedRoute - Reached default fallback, denying access');
        setCourseState({
          isLoading: false,
          courseStatus: 'inactive',
          hasAccess: false
        });

      } catch (error) {
        console.error('Error checking course access:', error);
        setCourseState({
          isLoading: false,
          courseStatus: 'error',
          hasAccess: false
        });
      }
    };

    checkCourseAccess();
  }, [user, authLoading]);

  // Still loading
  if (authLoading || courseState.isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center">
          <div className="flex items-center justify-center mb-4">
            <Crown className="text-purple-500 animate-bounce" size={32} />
            <Sparkles className="text-yellow-400 animate-pulse" size={24} />
          </div>
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-purple-600 text-xl font-bold mb-2">Loading Premium Experience</div>
          <div className="text-gray-600">Verifying your course access...</div>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Teacher account — route to the teacher portal (TeacherRoute owns the
  // active/inactive decision; it renders in place for inactive, so no loop).
  if (courseState.courseStatus === 'teacher') {
    return <Navigate to="/teacher" replace />;
  }

  // Trial signup, no course yet — the standalone /trial-status screen owns this
  // (not wrapped in ProtectedRoute, so no loop). hasAccess stays false above,
  // so no game / chat / dashboard / resource route ever renders for them.
  if (courseState.courseStatus === 'trial') {
    return <Navigate to="/trial-status" replace />;
  }

  // User document doesn't exist - redirect to select-plan
  if (courseState.courseStatus === 'no-document') {
    return <Navigate to="/select-plan" replace />;
  }

  // NEW: Add expired-pending case to render logic
  if (courseState.courseStatus === 'expired-pending') {
    return <Navigate to="/select-plan" replace />;
  }


  // No course or pending course - redirect to complete payment
  if (courseState.courseStatus === 'no-course' || courseState.courseStatus === 'pending') {
    return <Navigate to="/complete-subscription" replace />;
  }

  // Course completed - all sessions attended; lock games but allow account + renewal
  // (account is needed for report cards, payment method, child details, etc.)
  if (courseState.courseStatus === 'completed') {
    const currentPath = window.location.pathname;

    // Allow completed users to access renewal and account pages
    if (currentPath === '/course-renewal' || currentPath === '/account') {
      return children;
    }

    // For all other routes (games, dashboard, etc.), show the celebratory screen
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center max-w-md w-full">
          <div className="text-6xl mb-4">🎉</div>
          <div className="text-purple-600 text-2xl font-bold mb-3">Course Complete!</div>
          <div className="text-gray-700 mb-6">
            Congratulations on finishing your course! Ready to continue your Arabic learning journey?
          </div>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = '/course-renewal'}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-105"
            >
              Start Next Course
            </button>
            <button
              onClick={() => window.location.href = '/account'}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-all duration-200"
            >
              View Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Course expired - allow access to renewal, add children, and account pages
  if (courseState.courseStatus === 'expired') {
    const currentPath = window.location.pathname;
    
    // Allow expired users to access renewal and account pages
    if (currentPath === '/course-renewal' || currentPath === '/account' || currentPath === '/add-children') {
      return children; // Grant access to these specific pages
    }
    
    // For all other routes, show the expired screen with options
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center max-w-md w-full">
          <div className="text-orange-500 text-6xl mb-4">⏰</div>
          <div className="text-orange-600 text-xl font-bold mb-4">Course Ended</div>
          <div className="text-gray-700 mb-6">
            Your course has ended. Ready to continue your Arabic learning journey?
          </div>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = '/course-renewal'}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-105"
            >
              Start Next Course
            </button>
            <button
              onClick={() => window.location.href = '/account'}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-all duration-200"
            >
              View Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Course error - show error screen
  if (courseState.courseStatus === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center max-w-md w-full">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <div className="text-red-600 text-xl font-bold mb-4">Access Error</div>
          <div className="text-gray-700 mb-6">
            We're having trouble verifying your course access. Please try refreshing the page or contact support.
          </div>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200"
            >
              Refresh Page
            </button>
            <button
              onClick={() => window.location.href = 'mailto:support@yourdomain.com'}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-all duration-200"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Inactive course - redirect to payment
  if (courseState.courseStatus === 'inactive') {
    return <Navigate to="/complete-subscription" replace />;
  }

  // User has access - render protected content
  if (courseState.hasAccess) {
    return children;
  }

  // Fallback - redirect to complete subscription
  return <Navigate to="/complete-subscription" replace />;
}

export default ProtectedRoute;